import { useState } from "react";
import axios from "axios";
import { MapPin, Mail, Phone, Clock, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "../Reveal";
import { FIRM } from "../../data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection({ defaultService = "" }) {
  const [form, setForm] = useState({
    nombre: "",
    empresa: "",
    email: "",
    telefono: "",
    mensaje: "",
    servicio: defaultService,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.mensaje) {
      toast.error("Completa los campos requeridos.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        empresa: form.empresa.trim() || null,
        email: form.email.trim(),
        telefono: form.telefono.trim() || null,
        mensaje: form.mensaje.trim(),
        servicio: form.servicio || null,
      };
      await axios.post(`${API}/contact`, payload);
      setDone(true);
      toast.success("Mensaje enviado. Te contactaremos pronto.");
      setForm({ nombre: "", empresa: "", email: "", telefono: "", mensaje: "", servicio: defaultService });
    } catch (err) {
      const msg = err?.response?.data?.detail || "No pudimos enviar tu mensaje. Intenta nuevamente.";
      toast.error(typeof msg === "string" ? msg : "Error al enviar.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="home-contact" className="section-pad bg-white">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="gold-rule" />
            <span className="overline">Contacto</span>
          </div>
          <h2 className="font-serif text-navy-900 text-3xl md:text-4xl lg:text-5xl leading-tight">
            Conversemos sobre tu caso.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Cuéntanos brevemente lo que necesitas y un asesor revisará tu solicitud para responder con la ruta jurídica más adecuada.
          </p>

          <ul className="mt-10 space-y-5">
            <li className="flex gap-4">
              <MapPin size={18} className="mt-1 flex-shrink-0" style={{ color: "#C5A059" }} />
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Oficinas</p>
                <p className="mt-1 text-navy-900 font-medium leading-relaxed">
                  {FIRM.address.line1}<br />
                  {FIRM.address.line2} — {FIRM.address.city}
                </p>
              </div>
            </li>
            <li className="flex gap-4">
              <Mail size={18} className="mt-1 flex-shrink-0" style={{ color: "#C5A059" }} />
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Email</p>
                <a href={`mailto:${FIRM.email}`} className="mt-1 block text-navy-900 font-medium under-gold w-fit">
                  {FIRM.email}
                </a>
              </div>
            </li>
            <li className="flex gap-4">
              <Phone size={18} className="mt-1 flex-shrink-0" style={{ color: "#C5A059" }} />
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Teléfono</p>
                <p className="mt-1 text-navy-900 font-medium">{FIRM.phone}</p>
              </div>
            </li>
            <li className="flex gap-4">
              <Clock size={18} className="mt-1 flex-shrink-0" style={{ color: "#C5A059" }} />
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Horario</p>
                <p className="mt-1 text-navy-900 font-medium">{FIRM.hours}</p>
              </div>
            </li>
          </ul>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={100}>
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="bg-white border border-slate-200 p-7 md:p-10"
          >
            {done ? (
              <div data-testid="contact-success" className="text-center py-12">
                <CheckCircle2 size={56} className="mx-auto mb-4" style={{ color: "#C5A059" }} />
                <h3 className="font-serif text-2xl text-navy-900">Hemos recibido tu mensaje</h3>
                <p className="mt-3 text-slate-600 max-w-md mx-auto">
                  Un asesor revisará tu solicitud y te contactará en el menor tiempo posible.
                </p>
                <button
                  type="button"
                  onClick={() => setDone(false)}
                  className="mt-7 text-sm font-medium text-navy-900 under-gold"
                  data-testid="contact-send-another"
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Nombre *" testid="contact-nombre" value={form.nombre} onChange={update("nombre")} required />
                  <Field label="Empresa" testid="contact-empresa" value={form.empresa} onChange={update("empresa")} />
                  <Field type="email" label="Email *" testid="contact-email" value={form.email} onChange={update("email")} required />
                  <Field label="Teléfono" testid="contact-telefono" value={form.telefono} onChange={update("telefono")} />
                </div>

                <div className="mt-5">
                  <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    value={form.mensaje}
                    onChange={update("mensaje")}
                    required
                    rows={5}
                    data-testid="contact-mensaje"
                    className="w-full bg-white border border-slate-200 focus:border-navy-900 outline-none px-4 py-3 text-[15px] text-navy-900 transition-colors resize-none"
                    placeholder="Cuéntanos brevemente cómo podemos ayudarte…"
                  />
                </div>

                <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-slate-500">
                    Al enviar aceptas nuestra política de tratamiento de datos.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-navy-900 text-white text-sm font-medium rounded-sm hover:bg-navy-800 transition-colors disabled:opacity-60"
                  >
                    {submitting ? "Enviando…" : "Enviar mensaje"}
                    <Send size={15} />
                  </button>
                </div>
              </>
            )}
          </form>
        </Reveal>
      </div>

      {/* Map */}
      <div className="container-x mt-16">
        <Reveal>
          <div className="border border-slate-200 overflow-hidden">
            <iframe
              title="Mapa Ospino & Piñeres - Cartagena"
              data-testid="contact-map"
              src={FIRM.mapEmbed}
              className="w-full h-[380px] grayscale-[20%]"
              loading="lazy"
              allowFullScreen=""
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Field({ label, testid, type = "text", ...rest }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">
        {label}
      </label>
      <input
        type={type}
        data-testid={testid}
        {...rest}
        className="w-full bg-white border border-slate-200 focus:border-navy-900 outline-none px-4 py-3 text-[15px] text-navy-900 transition-colors"
      />
    </div>
  );
}